import { CheckCircle, Plus, RefreshCw, Target } from 'lucide-react';
import { Link } from 'wouter';
import { Button } from '../ui/button';
import { routes } from '../../lib/assessment/routes';
import type { AssessmentType } from '../../lib/apiClient';

/**
 * The dashboard's **Assessments** tab, copied from production.
 *
 * Three cards, in production's order, with production's headings, descriptions,
 * badges and button labels. A card the customer has completed offers "Take Free
 * Reassessment" in the outline variant; one they have not offers "Take Free
 * Assessment" in the primary variant.
 *
 * PARITY TODO: production links `/assessment`, `/work-fit/assessment` and
 * `/perspective/assessment` — three routes for three types. V2 serves one
 * `/assessment/:type`, which is the same destination reached differently and is
 * the routing shape V2 established long before this migration.
 */
const CARDS: ReadonlyArray<{
  type: AssessmentType;
  eyebrow: string;
  title: string;
  description: string;
}> = [
  {
    type: 'bottleneck',
    eyebrow: 'Bottleneck',
    title: 'High Performer Bottleneck Assessment',
    description:
      'Identify the internal friction patterns limiting your execution, clarity, recovery, and consistency.',
  },
  {
    type: 'work_fit',
    eyebrow: 'Work Fit',
    title: 'High Performer Work Fit Assessment',
    description:
      'Understand the work conditions, values, pace, and environment where your performance is most supported.',
  },
  {
    type: 'perspective',
    eyebrow: 'Perspective',
    title: 'High Performer Perspective Assessment',
    description:
      'Identify the interpretive patterns shaping your decisions, confidence, and performance output.',
  },
];

export function AssessmentsTab({ completed }: { completed: ReadonlySet<AssessmentType> }) {
  return (
    <div className="space-y-8 pt-6" data-testid="dashboard-tab-assessments">
      <section>
        <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
          <Target className="w-5 h-5 text-primary" aria-hidden="true" />
          Individual Assessments
        </h2>

        <div className="grid md:grid-cols-2 gap-4">
          {CARDS.map((card) => {
            const done = completed.has(card.type);
            return (
              <div
                key={card.type}
                className="p-6 rounded-xl border border-border bg-card space-y-4 flex flex-col"
                data-testid="assessment-card"
                data-assessment-type={card.type}
              >
                <div className="space-y-1">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground shrink-0">
                      {card.eyebrow}
                    </span>
                    {done ? (
                      <span
                        className="inline-flex items-center rounded-md border border-emerald-600/30 bg-emerald-600/20 px-2 py-0.5 text-xs text-emerald-400 shrink-0"
                        data-testid="assessment-status"
                      >
                        <CheckCircle className="w-3 h-3 mr-1" aria-hidden="true" />
                        Completed
                      </span>
                    ) : (
                      <span
                        className="inline-flex items-center rounded-md border border-border px-2 py-0.5 text-xs text-muted-foreground shrink-0"
                        data-testid="assessment-status"
                      >
                        Not Started
                      </span>
                    )}
                  </div>
                  <p className="font-bold text-base leading-tight">{card.title}</p>
                </div>

                <p className="text-sm text-muted-foreground flex-1">{card.description}</p>

                <div className="space-y-2 mt-auto">
                  <Link
                    href={routes.assessment(card.type)}
                    data-testid="assessment-start"
                    className="focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  >
                    <Button
                      variant={done ? 'outline' : 'default'}
                      className="w-full gap-2"
                      tabIndex={-1}
                    >
                      {done ? (
                        <>
                          <RefreshCw className="w-4 h-4" aria-hidden="true" />
                          Take Free Reassessment
                        </>
                      ) : (
                        <>
                          <Plus className="w-4 h-4" aria-hidden="true" />
                          Take Free Assessment
                        </>
                      )}
                    </Button>
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
